import React, {
  ReactNode,
  useState
} from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';

import {
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography
} from '@material-ui/core';
import {
  Block as IconDisable,
  HowToReg as IconEnable
} from '@material-ui/icons';

import {
  EditIcon,
  TableActionIcon
} from '@checkit/react-components/build/components/Icons';

import AlertDialog from '../../components/alert/AlertDialog';
import MaterialUiLink from '../../components/MaterialUiLink';
import { useAxiosDispatch } from '../../middleware/axios';
import { createSnackbar } from '../../notifications/actions';
import {
  isUpdateUserDefinedRoleSuccess,
  updateUserDefinedRole
} from '../actions';
import { UserDefinedRole } from '../state';

import { messages } from './UserDefinedRoleList.translations';

/** Gets the relative url to the user-defined role's edit page. */
const getRoleUrl = (role: UserDefinedRole) => `/user-defined-roles/${role.code}`;

export function UserDefinedRoleActionsImpl({ role }: {
  /** The user-defined role for which actions are being displayed. */
  role: UserDefinedRole;
}) {
  const { formatMessage } = useIntl( );
  const dispatch = useAxiosDispatch( );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [disableModalOpen, setDisableModalOpen] = useState(false);

  const isMenuOpen = anchorEl != null;

  // Activity Toggle Button

  const toggleActivityIcon = role.isActive ? (<IconDisable />) : (<IconEnable />);

  const toggleActivityText = role.isActive
    ? formatMessage(messages['userRoles.list.disable'])
    : formatMessage(messages['userRoles.list.enable']);

  // Activity Toggle Confirmation Modal

  const toggleActivityModalTitle = role.isActive
    ? formatMessage(messages['userRoles.list.actions.disable.modalTitle'])
    : formatMessage(messages['userRoles.list.actions.enable.modalTitle']);

  const toggleActivityModalMessage = role.isActive
    ? messages['userRoles.list.actions.disable.areYouSure']
    : messages['userRoles.list.actions.enable.areYouSure'];

  const toggleActivityModalContent = formatMessage(toggleActivityModalMessage, {
    b: (...chunks: ReactNode[]) => (<b>{chunks}</b>),
    displayName: role.name
  });

  const toggleActivityModalInner = role.isActive ? (function getDisableModalContent( ) {
    const descriptionBase = messages['userRoles.list.actions.disable.areYouSure.description'];
    const descriptionContent = formatMessage(descriptionBase, {
      b: (...chunks: ReactNode[]) => (<b>{chunks}</b>)
    });
    return (
      <>
        <Typography variant="body1">{toggleActivityModalContent}</Typography>
        <br />
        <Typography variant="body2" color="textSecondary">{descriptionContent}</Typography>
      </>
    );
  })( ) : (<Typography variant="body1">{toggleActivityModalContent}</Typography>);

  // Event Handlers

  const onClickOpenMenu = (event: React.MouseEvent<HTMLElement>) => void setAnchorEl(event.currentTarget);
  const onClickCloseMenu = ( ) => void setAnchorEl(null);
  const onToggleActivityCancelled = ( ) => void setDisableModalOpen(false);

  function onToggleActivityConfirmed( ) {
    setDisableModalOpen(false);
    const isTargetToEnable = !role.isActive;
    const action = updateUserDefinedRole(role.code, { isActive: isTargetToEnable });

    dispatch(action).then(function handleResult(result) {
      if (isUpdateUserDefinedRoleSuccess(result)) {
        const message = isTargetToEnable
          ? messages['userRoles.list.enable.success']
          : messages['userRoles.list.disable.success'];
        dispatch(createSnackbar({
          message: formatMessage(message),
          options: { variant: 'success' }
        }));
      } else {
        // to be handled by the catch block
        throw result.error || new Error(`Unexpected resulting action: "${result.type}"`);
      }
    }).catch(function handleError(/* err: unknown */) {
      const message = isTargetToEnable
        ? messages['userRoles.list.enable.failure']
        : messages['userRoles.list.disable.failure'];
      dispatch(createSnackbar({
        message: formatMessage(message),
        options: { variant: 'error' }
      }));
    });
  }
  function onClickToggleActivity( ) {
    setDisableModalOpen(true);
    onClickCloseMenu( );
  }

  return (
    <>
      <IconButton
        aria-label={formatMessage(messages['userRoles.list.actions'])}
        aria-controls="long-menu"
        aria-haspopup="true"
        onClick={onClickOpenMenu}
      >
        <TableActionIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={onClickCloseMenu}
      >
        <MenuItem
          onClick={onClickCloseMenu}
          component={MaterialUiLink}
          to={getRoleUrl(role)}
        >
          <ListItemIcon><EditIcon /></ListItemIcon>
          {formatMessage(messages['userRoles.list.update'])}
        </MenuItem>

        <MenuItem onClick={onClickToggleActivity}>
          <ListItemIcon >{toggleActivityIcon}</ListItemIcon >
          {toggleActivityText}
        </MenuItem>
      </Menu>

      <AlertDialog
        title={toggleActivityModalTitle}
        open={disableModalOpen}
        text={toggleActivityModalInner}

        onAccept={onToggleActivityConfirmed}
        onCancel={onToggleActivityCancelled}
      />
    </>
  );
}
export const UserDefinedRoleActions = connect(
  undefined,
  (dispatch) => ({ dispatch })
)(UserDefinedRoleActionsImpl);
